<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">{{ t("audit.title") }}</h1>
      <div class="text-light-gray text-sm">
        {{ t("audit.subTitle") }}
      </div>
    </div>
    <div v-if="isLoading" class="flex h-[60vh] justify-center">
      <LoadingIcon :isLoading="isLoading" />
    </div>
    <div class="flex flex-col gap-4" v-if="!isLoading">
      <FilterBox styleInputOptions="md:grid-cols-3">
        <template #inputOptions>
          <CustomInput
            v-model="filterUserId"
            maxlength="50"
            :title="t('audit.filter.userId')"
            :placeholder="t('audit.filter.userId')"
          >
            <IconManager name="Search" class="text-light-gray" />
          </CustomInput>

          <CustomInput
            v-model="filterEntityName"
            maxlength="50"
            :title="t('audit.filter.entityName')"
            :placeholder="t('audit.filter.entityName')"
          >
            <IconManager name="Search" class="text-light-gray" />
          </CustomInput>

          <CustomInput
            v-model="filterExternalEntityId"
            maxlength="50"
            :title="t('audit.filter.externalEntityId')"
            :placeholder="t('audit.filter.externalEntityId')"
          >
            <IconManager name="Search" class="text-light-gray" />
          </CustomInput>

          <CustomDatePicker
            :title="t('audit.filter.startDate')"
            v-model="filterStartDate"
            :clearable="true"
            :placeholder="t('audit.filter.startDate')"
          />

          <CustomDatePicker
            :title="t('audit.filter.endDate')"
            :placeholder="t('audit.filter.endDate')"
            v-model="filterEndDate"
            :format-locale="pt"
            :clearable="true"
            :minDate="filterStartDate"
          />
        </template>
        <template #filterStateOptions>
          <FilterState
            v-for="option in auditStates"
            :label="option.getLocalization()"
            :value="option.value"
            :color="option.color"
            v-model="filterState"
          />
        </template>
        <template #filterButtons>
          <div class="flex flex-row sm:w-[35%] sm:ml-auto gap-2">
            <CustomCleanFilterButton
              class="flex-1"
              title="Limpar filtros"
              @click="clearFilters"
              :numberActivedFilters="getActiveFiltersCount()"
              :hasActiveFilters="hasActiveFilters()"
            />

            <CustomApplyFilterButton
              class="flex-1"
              title="Filtrar"
              @click="applyFilters"
              :hasUnappliedFilters="hasUnappliedFilters()"
            />
          </div>
        </template>
        <template #filterTagBox>
          <AppliedFilterBox :hasActiveFilters="hasActiveFilters()">
            <template #filterTag>
              <FilterTag
                :title="t('audit.filter.userId')"
                :value="appliedFilters.userId"
                :isVisible="filterUserId"
              />

              <FilterTag
                :title="t('audit.filter.entityName')"
                :value="appliedFilters.entityName"
                :isVisible="filterEntityName"
              />

              <FilterTag
                :title="t('audit.filter.externalEntityId')"
                :value="appliedFilters.externalEntityId"
                :isVisible="filterExternalEntityId"
              />

              <FilterTag
                :title="t('audit.filter.startDate')"
                :value="appliedFilters.startDate"
                :isVisible="filterStartDate"
              />

              <FilterTag
                :title="t('audit.filter.endDate')"
                :value="appliedFilters.endDate"
                :isVisible="filterEndDate"
              />

              <FilterTag
                :title="t('audit.filter.state')"
                :value="appliedFilters.state"
                :isVisible="filterState"
              />
            </template>
          </AppliedFilterBox>
        </template>
      </FilterBox>

      <CustomTable
        :data="audits"
        :total-entries="tableState.totalEntries"
        :is-loading="isLoadingTable"
        :pagination="{ goToPageCallBack: goToPage }"
        :pageSizeOptions="{ changeCallBack: changePageSize }"
        :sort-call-back="sortColumn"
        :columns="[
          { key: 'id', title: t('common.id'), sortable: false },
          {
            key: 'userId',
            title: t('audit.table.userId'),
            sortable: true,
          },
          {
            key: 'actionDate',
            title: t('audit.table.actionDate'),
            sortable: true,
          },
          {
            key: 'entityName',
            title: t('audit.table.entityName'),
            sortable: false,
          },
          {
            key: 'entityId',
            title: t('audit.table.entityId'),
            sortable: false,
          },
          {
            key: 'externalEntityId',
            title: t('audit.table.externalEntityId'),
            sortable: false,
          },
          {
            key: 'changedColumns',
            title: t('audit.table.changedColumns'),
            sortable: false,
            render: (columns: string[]) =>
              h(
                'div',
                { class: 'flex flex-row gap-1 flex-wrap' },
                columns.map((column) =>
                  h(
                    'span',
                    {
                      key: column,
                      class: 'rounded-xl px-2 text-primary bg-secondary',
                    },
                    column,
                  ),
                ),
              ),
          },
          {
            key: 'actionType',
            title: t('audit.table.actionType'),
            sortable: true,
            render: (value: AuditState) =>
              h(
                'div',
                {
                  class:
                    'flex flex-row items-center gap-2 rounded-xl px-2 w-max',
                  style: { backgroundColor: value.lightColor },
                },
                [
                  h('span', {
                    class: 'w-2.5 h-2.5 rounded-full inline-block',
                    style: { backgroundColor: value.color },
                  }),
                  h(
                    'span',
                    { class: 'font-bold', style: { color: value.color } },
                    value.getLocalization(),
                  ),
                ],
              ),
          },
          {
            key: 'actions',
            title: t('common.table.actions'),
            sortable: false,
            render: (_, row: Audit) =>
              h(
                'div',
                {
                  class: 'flex flex-row gap-2 items-center ',
                },
                [
                  h(
                    CustomButton,
                    {
                      class: 'bg-secondary! text-primary border-none! p-2!',
                      onClick: () => {
                        selectedAudit = row;
                        isDialogOpen = true;
                      },
                    },
                    () =>
                      h(IconManager, {
                        name: 'Eye',
                      }),
                  ),
                ],
              ),
          },
        ]"
      />
      <CustomDialog
        v-model="isDialogOpen"
        :title="t('audit.dialog.title')"
        :cancelText="t('common.close')"
        :is-submit-button-visible="false"
        @cancel="isDialogOpen = false"
      >
        <div class="flex flex-col gap-3">
          <CustomMultipleInput
            :title="t('audit.table.oldValue')"
            readonly
            :model-value="selectedAudit?.oldValues"
          />
          <CustomMultipleInput
            :title="t('audit.table.newValue')"
            readonly
            :model-value="selectedAudit?.newValues"
          />
        </div>
      </CustomDialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { pt } from "date-fns/locale";
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import CustomDatePicker from "@/core/components/CustomDatePicker.vue";
import CustomInput from "@/core/components/CustomInput.vue";
import CustomDialog from "@/core/components/CustomDialog.vue";
import CustomButton from "@/core/components/CustomButton.vue";
import CustomMultipleInput from "@/core/components/CustomMultipleInput.vue";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import CustomTable from "@/core/components/table/CustomTable.vue";
import { useI18n } from "vue-i18n";
import { auditRemoteDataSource } from "@/core/models/global-variables";
import { h, onMounted, ref, watch } from "vue";
import { Audit } from "../models/audit";
import { AuditState } from "../models/audit-state";

//Filter
import CustomCleanFilterButton from "@/core/components/filter/CustomCleanFilterButton.vue";
import CustomApplyFilterButton from "@/core/components/filter/CustomApplyFilterButton.vue";
import AppliedFilterBox from "@/core/components/filter/AppliedFilterBox.vue";
import FilterBox from "@/core/components/filter/FilterBox.vue";
import FilterTag from "@/core/components/filter/FilterTag.vue";
import FilterState from "@/core/components/filter/FilterState.vue";

const { t } = useI18n();
const { showError } = useToast();

const audits = ref<Audit[]>([]);
const auditStates = AuditState.states;

const selectedAudit = ref<Audit | null>(null);

const isLoading = ref<boolean>(true);

const isLoadingTable = ref<boolean>(false);

onMounted(() => {
  loadInitialData();
});

//Filter variables
const filterUserId = ref<string>("");
const filterEntityName = ref<string>("");
const filterExternalEntityId = ref<string>("");
const filterStartDate = ref<Date | null>(null);
const filterEndDate = ref<Date | null>(null);
const filterState = ref<string[]>([]);
const appliedFilters = ref<{
  userId: string;
  startDate: string;
  endDate: string;
  state: string[];
  entityName: string;
  externalEntityId: string;
}>({
  userId: "",
  startDate: "",
  endDate: "",
  state: [],
  entityName: "",
  externalEntityId: "",
});

watch(filterStartDate, (newStartDate) => {
  if (
    newStartDate &&
    filterEndDate.value &&
    filterEndDate.value < newStartDate
  ) {
    filterEndDate.value = null;
  }
});

//TableState
const tableState = ref<{
  pageSize: number;
  totalEntries: number;
}>({
  pageSize: 10,
  totalEntries: 0,
});

//Dialog
const isDialogOpen = ref(false);

async function applyFilters() {
  try {
    isLoadingTable.value = true;
    const response = await auditRemoteDataSource.getAudits({
      userId: filterUserId.value,
      startDate: filterStartDate.value?.toISOString() ?? "",
      endDate: filterEndDate.value?.toISOString() ?? "",
      entityName: filterEntityName.value,
      externalEntityId: filterExternalEntityId.value,
      actionTypes: filterState.value,
      pageSize: tableState.value.pageSize,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    appliedFilters.value = {
      userId: filterUserId.value,
      startDate: filterStartDate.value?.toISOString() ?? "",
      endDate: filterEndDate.value?.toISOString() ?? "",
      entityName: filterEntityName.value,
      externalEntityId: filterExternalEntityId.value,
      state: filterState.value,
    };
    tableState.value.totalEntries = response.data.totalItems;
    audits.value = response.data.items;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

async function clearFilters() {
  isLoadingTable.value = true;

  try {
    const response = await auditRemoteDataSource.getAudits({
      pageNumber: 1,
      pageSize: tableState.value.pageSize,
    });

    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }

    tableState.value.totalEntries = response.data.totalItems;
    audits.value = response.data.items;
    appliedFilters.value = {
      userId: "",
      entityName: "",
      externalEntityId: "",
      startDate: "",
      endDate: "",
      state: [],
    };

    filterUserId.value = "";
    filterEntityName.value = "";
    filterExternalEntityId.value = "";
    filterStartDate.value = null;
    filterEndDate.value = null;
    filterState.value = [];
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

async function sortColumn(
  key: string,
  orderDirection: string,
): Promise<boolean> {
  try {
    isLoadingTable.value = true;
    const response = await auditRemoteDataSource.getAudits({
      userId: filterUserId.value,
      startDate: filterStartDate.value?.toISOString() ?? "",
      endDate: filterEndDate.value?.toISOString() ?? "",
      entityName: filterEntityName.value,
      externalEntityId: filterExternalEntityId.value,
      actionTypes: filterState.value,
      pageSize: tableState.value.pageSize,
      orderBy: key,
      orderDirection: orderDirection,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    tableState.value.totalEntries = response.data.totalItems;
    audits.value = response.data.items;
    return true;
  } catch (error: any) {
    showError({ title: error });
    return false;
  } finally {
    isLoadingTable.value = false;
  }
}

function hasActiveFilters(): boolean {
  return (
    appliedFilters.value.userId.length > 0 ||
    appliedFilters.value.startDate.length > 0 ||
    appliedFilters.value.endDate.length > 0 ||
    appliedFilters.value.entityName.length > 0 ||
    appliedFilters.value.externalEntityId.length > 0 ||
    appliedFilters.value.state.length > 0
  );
}

function hasUnappliedFilters(): boolean {
  const fieldsChanged =
    appliedFilters.value.userId !== filterUserId.value ||
    appliedFilters.value.entityName !== filterEntityName.value ||
    appliedFilters.value.externalEntityId !== filterExternalEntityId.value ||
    appliedFilters.value.startDate !==
      (filterStartDate.value?.toDateString() ?? "") ||
    appliedFilters.value.endDate !==
      (filterEndDate.value?.toDateString() ?? "");

  const stateChanged = !arraysEqualUnordered(
    filterState.value,
    appliedFilters.value.state,
  );

  return fieldsChanged || stateChanged;
}

function arraysEqualUnordered(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
}

function getActiveFiltersCount(): number {
  return Object.values(appliedFilters.value).filter((value) => value.length > 0)
    .length;
}

async function loadInitialData() {
  isLoading.value = true;

  try {
    const response = await auditRemoteDataSource.getAudits({
      pageSize: tableState.value.pageSize,
      pageNumber: 1,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    tableState.value.totalEntries = response.data.totalItems;
    audits.value = response.data.items;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function goToPage(page: number, pageSize: number) {
  isLoadingTable.value = true;

  try {
    const response = await auditRemoteDataSource.getAudits({
      startDate: appliedFilters.value.startDate,
      endDate: appliedFilters.value.endDate,
      userId: appliedFilters.value.userId,
      actionTypes: appliedFilters.value.state,
      entityName: appliedFilters.value.entityName,
      externalEntityId: appliedFilters.value.externalEntityId,
      pageSize: pageSize,
      pageNumber: page,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    tableState.value.pageSize = pageSize;
    tableState.value.totalEntries = response.data.totalItems;
    audits.value = response.data.items;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}

async function changePageSize(pageSize: number, currentPage: number) {
  isLoadingTable.value = true;
  try {
    const response = await auditRemoteDataSource.getAudits({
      startDate: appliedFilters.value.startDate,
      endDate: appliedFilters.value.endDate,
      userId: appliedFilters.value.userId,
      actionTypes: appliedFilters.value.state,
      entityName: appliedFilters.value.entityName,
      externalEntityId: appliedFilters.value.externalEntityId,
      pageSize: pageSize,
      pageNumber: currentPage,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    tableState.value.pageSize = pageSize;
    tableState.value.totalEntries = response.data.totalItems;
    audits.value = response.data.items;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingTable.value = false;
  }
}
</script>
